import { Filters as FiltersType } from '@/domain';
import { formatISODate, isPast } from 'shared/util/datetime-fns';
import { Autocomplete, AutocompleteOption } from 'shared/search/common/Autocomplete';
import { Button, TextField } from '@fabric-ds/react';
import { DatePickerCalendar, DatePickerInput, DatePickerPopover, DateRangePicker } from '@finn-no/legacy-fabric-datepicker';
import { useEffect, useState } from 'react';
import { Dropdown } from 'shared/search/common/Dropdown';
import { BedroomFilter } from 'shared/search/fhh/BedroomFilter';
import { FhhSearchSubmit } from 'shared/search/fhh/FhhSearchSubmit';
import getConfig from 'next/config';
import { fhhAutocompleteMapper } from 'shared/search/common/autocompleteMapper';

type FhhSearchProps = {
    filters: FiltersType;
};

const {
    publicRuntimeConfig: { apiExternalUrl },
} = getConfig();

export const FhhSearch = ({ filters }: FhhSearchProps) => {
    const emptyDestination: AutocompleteOption = {
        id: '',
        text: '',
    };

    const populateDestination = () =>
        filters.city
            ? {
                  id: `${filters.city}, ${filters.country}`,
                  text: `${filters.city}, ${filters.country}`,
                  city: filters.city,
                  country: filters.country ?? undefined,
                  bounds: {
                      southwest_lat: Number(filters.lat_sw),
                      southwest_lng: Number(filters.lng_sw),
                      northeast_lat: Number(filters.lat_ne),
                      northeast_lng: Number(filters.lng_ne),
                  },
              }
            : emptyDestination;

    const dateOrNull = (date: string): Date | null => (date ? new Date(date) : null);
    const [fromDate, setFromDate] = useState(dateOrNull(filters.booking_from ?? ''));
    const [toDate, setToDate] = useState(dateOrNull(filters.booking_to ?? ''));
    const [destination, setDestination] = useState<AutocompleteOption>(populateDestination);
    const [bedrooms, setBedrooms] = useState(filters.no_of_bedrooms_from ?? 0);
    const [beds, setBeds] = useState(filters.no_of_beds_from ?? 0);

    useEffect(() => {
        setFromDate(dateOrNull(filters.booking_from ?? ''));
        setToDate(dateOrNull(filters.booking_to ?? ''));
        setDestination(populateDestination);
        setBedrooms(filters.no_of_bedrooms_from ?? 0);
        setBeds(filters.no_of_beds_from ?? 0);
    }, [filters]);

    const handleDatesChange = ({ startDate, endDate }) => {
        if (startDate) {
            toDate ? (startDate >= toDate ? setToDate(null) : null) : null;
            setFromDate(dateOrNull(formatISODate(startDate)));
        }
        endDate ? setToDate(dateOrNull(formatISODate(endDate))) : null;
    };

    const onAccommodationChanged = (valueChanged): void => {
        if (valueChanged.no_of_bedrooms_from !== undefined) {
            setBedrooms(valueChanged.no_of_bedrooms_from);
        }
        if (valueChanged.no_of_beds_from !== undefined) {
            setBeds(valueChanged.no_of_beds_from);
        }
    };

    const emptyFields = () => {
        setDestination(emptyDestination);
        setBeds(0);
        setBedrooms(0);
        setFromDate(null);
        setToDate(null);
    };

    const bedroomText = `${bedrooms ? `${bedrooms} soverom` : 'Alle soverom'}, ${beds ? `${beds} sengeplasser` : 'alle sengeplasser'}`;

    return (
        <section className="bg-aqua-50 rounded-8 p-32 mb-24" data-testid="fhhNewSearch" aria-label="Feriehjem og hytter til leie, nytt søk">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-16">
                <Autocomplete
                    id="newSearchDestination"
                    label="Hvor vil du reise?"
                    placeholder="Hvor som helst"
                    defaultValue={
                        destination?.city
                            ? `${destination.city}, ${destination.country}`
                            : destination?.country
                            ? `${destination.country}`
                            : ''
                    }
                    minChars={2}
                    url={apiExternalUrl + '/autocomplete?query='}
                    mapOptions={fhhAutocompleteMapper}
                    // @ts-expect-error TS2322: Type 'AutocompleteOption | null' is not assignable to type 'SetStateAction<AutocompleteOption>'.
                    onSelect={(option) => setDestination(option)}
                    className="lg:col-span-2"
                />
                <DateRangePicker
                    startDate={fromDate}
                    endDate={toDate}
                    isDayDisabled={isPast}
                    minimumNights={1}
                    onChange={handleDatesChange}>
                    <div className="grid grid-cols-2 gap-x-16">
                        <DatePickerInput
                            dateField="startDate"
                            as={TextField}
                            id="dateFhhCheckin"
                            label="Innsjekk"
                            aria-label="Innsjekk"
                            placeholder="Fra dato"
                            data-testid="dateFhhCheckin"
                        />
                        <DatePickerInput
                            dateField="endDate"
                            as={TextField}
                            id="dateFhhCheckout"
                            label="Utsjekk"
                            aria-label="Utsjekk"
                            placeholder="Til dato"
                            data-testid="dateFhhCheckout"
                        />
                    </div>
                    {/* @ts-ignore TS2590: Expression produces a union type that is too complex to represent. */}
                    <DatePickerPopover className="date-popover">
                        <DatePickerCalendar numberOfMonths={2} />
                    </DatePickerPopover>
                </DateRangePicker>
                <Dropdown id="bedsAndBedrooms" label="Soverom og sengeplasser" text={bedroomText}>
                    <BedroomFilter
                        filters={{ no_of_beds_from: beds, no_of_bedrooms_from: bedrooms }}
                        onFilterChanged={onAccommodationChanged}
                    />
                </Dropdown>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-5">
                <FhhSearchSubmit
                    destinationData={destination}
                    startDate={fromDate}
                    endDate={toDate}
                    numOfBeds={beds}
                    numOfBedrooms={bedrooms}
                    resultUrl="/reise/feriehus-hytteutleie/resultat/"
                />
                <Button className="hidden md:block mt-32" onClick={emptyFields} data-testid="clearSearchFields" link>
                    Nullstill felter
                </Button>
            </div>
            {/*<div className="text-16 text-gray-500 mt-16">
                Tips! Legger du inn datoer for oppholdet, så vil du se nøyaktige priser i søket
            </div>*/}
        </section>
    );
};
